import { useAppTFunction } from '@retail/app/i18n';
import { DateFilter, ProductGroupFilter, SideFilters, ToggleGroup } from '@retail/components';
import { MgSupplierItemSearchField } from '@retail/products/components';
import { useFilters } from '@retail/products/hooks';
import { ExcludeVatSwitch, SelectDiscountGroup } from '@retail/my-assortment/components';
import { getISO_8601Date } from '@retail/utils';
import { CHANGE_RANGE_MAX, CHANGE_RANGE_MIN, ValueSpanSlider } from './ValueSpanSlider';
import { PriceControlFilterOptions, priceControlFilters } from '@retail/my-assortment/utils';
import { DiscountGroup } from '@retail/products/types';
import { useDisclosure } from '@shared/hooks';
import { HierarchyFilterContext } from '@retail/my-assortment/types';

interface Props {
  showFilters: boolean;
  includeVat: boolean;
  filterContext?: HierarchyFilterContext;
  isLoading: boolean;
  toggleVat: () => void;
}

export function PriceControlFilters({
  showFilters,
  includeVat,
  filterContext,
  isLoading,
  toggleVat,
}: Props) {
  const t = useAppTFunction();
  const { filters, setFilters } = useFilters<PriceControlFilterOptions>(priceControlFilters);
  const { isOpen: showProductGroups, onToggle: toggleProductGroups } = useDisclosure();

  const toChangeRange = (newValue: number[]) =>
    newValue[0] === CHANGE_RANGE_MIN && newValue[1] === CHANGE_RANGE_MAX
      ? undefined
      : newValue.map(String);

  return (
    <SideFilters showFilters={showFilters}>
      <ExcludeVatSwitch
        justifyContent="space-between"
        toggleVat={includeVat}
        handleToggleVat={toggleVat}
      />
      <ToggleGroup
        variant="pill"
        fullWidth
        size="small"
        label={t('myAssortment.priceControl.handledItems')}
        labelDirection="vertical"
        options={[
          { label: t('myAssortment.priceControl.unhandledItems'), value: 'UNHANDLED' },
          { label: t('myAssortment.priceControl.handledItems'), value: 'HANDLED' },
        ]}
        value={filters.isUnhandled ? 'HANDLED' : 'UNHANDLED'}
        onChange={(value: 'HANDLED' | 'UNHANDLED') =>
          setFilters({ isUnhandled: value === 'HANDLED' ? true : undefined })
        }
      />
      <ToggleGroup
        variant="pill"
        fullWidth
        size="small"
        label={t('myAssortment.priceControl.trigger.changeTrigger')}
        labelDirection="vertical"
        options={[
          { label: t('myAssortment.filter.all'), value: 'ALL' },
          { label: t('myAssortment.columns.purchasePrice.price'), value: 'AGREEMENT_PRICE' },
          { label: t('myAssortment.columns.salesPrice'), value: 'PRICE_SUGGESTION' },
        ]}
        value={filters.changeTrigger ?? 'ALL'}
        onChange={(value: string) =>
          setFilters({ changeTrigger: value === 'ALL' ? undefined : value })
        }
      />
      <MgSupplierItemSearchField
        size="small"
        searchTerm={filters.searchQuery ?? ''}
        onSubmitSearch={(query: string) => setFilters({ searchQuery: query })}
      />
      <SelectDiscountGroup
        discountGroups={filterContext?.discountGroupsForSupplier ?? []}
        selectedDiscountGroup={filters.discountGroupCode}
        selectDiscountGroup={(value: string | number | null) =>
          setFilters({ discountGroupCode: value ? value.toString() : null })
        }
        getDiscountGroupId={(group: DiscountGroup | null) => group?.id.toString() ?? null}
        isLoading={isLoading}
      />
      <ProductGroupFilter
        productGroups={isLoading ? undefined : filterContext?.productGroups ?? []}
        selectedProductGroup={
          filterContext?.productGroups.find(
            (pg) => pg.groupNumber === filters.productGroupNumber
          ) ?? null
        }
        selectProductGroup={(group: string | null) =>
          setFilters({ productGroupNumber: group ?? null })
        }
        showProductGroups={showProductGroups}
        toggleShowProductGroups={toggleProductGroups}
      />
      <DateFilter
        label={t('myAssortment.priceControl.changeDate')}
        value={filters.validFrom ? new Date(filters.validFrom) : null}
        onChange={(newDate) =>
          setFilters({ validFrom: newDate ? getISO_8601Date(newDate) : undefined })
        }
      />
      <ValueSpanSlider
        label={t('myAssortment.priceControl.purchasePriceChange')}
        valueRange={filters.purchasePriceChangeRange.map(Number)}
        setValueRange={(newValue: number[]) =>
          setFilters({ purchasePriceChangeRange: toChangeRange(newValue) })
        }
      />
      <ValueSpanSlider
        label={t('myAssortment.priceControl.salesPriceChange')}
        valueRange={filters.salesPriceChangeRange.map(Number)}
        setValueRange={(newValue: number[]) =>
          setFilters({ salesPriceChangeRange: toChangeRange(newValue) })
        }
      />
      <ValueSpanSlider
        label={t('myAssortment.priceControl.coverageDegreeChange')}
        valueRange={filters.covergeDegreeChangeRange.map(Number)}
        setValueRange={(newValue: number[]) =>
          setFilters({ covergeDegreeChangeRange: toChangeRange(newValue) })
        }
      />
    </SideFilters>
  );
}
